/**
 * =============================================================================
 * App update payload helpers (FE issue #25)
 * =============================================================================
 *
 * Kept out of the modal component so the rules are unit-testable on their own
 * (and so the component file only exports a component).
 */

import type { AppDetail, AppUpdate } from '../services/api'

export interface EditedAppFields {
  name: string
  description: string
  category: string
  imageRef: string
  deviceTypeIds: string[]
  configYaml?: string
  appParametersYaml?: string
}

/**
 * Build the PUT payload from the edited values, omitting everything that did
 * not change. Exported for tests: sending an unchanged `docker_image_url` or
 * `config_yaml` would needlessly push the app into re-approval.
 */
export function buildUpdatePayload(app: AppDetail, edited: EditedAppFields): AppUpdate {
  const payload: AppUpdate = {}
  const originalDeviceTypes = [...(app.supported_device_type_ids || [])].sort()
  const nextDeviceTypes = [...edited.deviceTypeIds].sort()

  if (edited.name.trim() && edited.name.trim() !== app.name) payload.name = edited.name.trim()
  if (edited.description !== (app.description || '')) payload.description = edited.description
  if (edited.category && edited.category !== app.category) payload.category = edited.category
  if (edited.imageRef.trim() && edited.imageRef.trim() !== app.docker_image_url) {
    payload.docker_image_url = edited.imageRef.trim()
  }
  if (originalDeviceTypes.join(',') !== nextDeviceTypes.join(',')) {
    payload.supported_device_type_ids = edited.deviceTypeIds
  }
  if (edited.configYaml !== undefined) payload.config_yaml = edited.configYaml
  if (edited.appParametersYaml !== undefined) {
    payload.app_parameters_yaml = edited.appParametersYaml
  }
  return payload
}

/** True when the payload triggers the backend's re-approval rule. */
export function payloadNeedsReapproval(payload: AppUpdate): boolean {
  return payload.docker_image_url !== undefined || payload.config_yaml !== undefined
}

